import './RaspberryPi.css'

import SEO from '../../../../Components/SEO/SEO'
import PageTitle from '../../../../Hooks/PageTitle';

import raspberrypi_image from '../../../../Media/Blog.Media/Guides.Media/RaspberryPi.Media/raspberrypi_image.jpg'
import raspberrypi_pins from '../../../../Media/Blog.Media/Guides.Media/RaspberryPi.Media/raspberrypi_pins.jpg'

const RaspberryPi = () => {
    PageTitle('Raspberry Pi Setup Guide');
    
	return (
		<>
			<SEO 
                title="Complete Raspberry Pi Setup Guide for Beginners"
                description="Learn how to set up a Raspberry Pi from scratch! Complete guide covering initial setup, Raspberry Pi OS installation, SSH access, GPIO basics, and your first Python hardware projects."
                url="https://yassinebazgour.vercel.app/blog/guides/raspberrypi-setup-complete-beginners"
                keywords="Raspberry Pi tutorial, Raspberry Pi setup, Raspberry Pi OS, Raspberry Pi Python, GPIO tutorial, embedded systems, IoT Raspberry Pi, Python hardware projects"
            />

			<section className='raspberrypi-section'>

                <div className='raspberrypi-head'>

					<span className='raspberrypi-title'>
                        Complete Raspberry Pi Setup Guide for Beginners<br />
                    </span>
					
					<div className='raspberrypi-timing-data-container'>
						<span className='time-stamp'>
                            <i className='fas fa-calendar'></i> July 2026
                        </span> 

						<span className='reading-time'>
                            <i className='fas fa-clock'></i> 45-60 min read
                        </span>
					</div>
					
					<div className='raspberrypi-key-words-container'>
						<span className='raspberrypi-key-word'>Raspberry Pi</span>
						<span className='raspberrypi-key-word'>Python</span>
						<span className='raspberrypi-key-word'>GPIO</span>
						<span className='raspberrypi-key-word'>IoT</span>
					</div>
					
					<img 
                        src={raspberrypi_image} 
                        className='raspberrypi-image' 
                        alt="Raspberry Pi board"
                    />

				</div>


				<div className='raspberrypi-content'>

					<p>
						The Raspberry Pi is a full computer the size of a credit card, and unlike Arduino, it runs a 
						real operating system. That means you get a desktop, a file system, and Python already 
						installed, but it also means the first setup step is different: you're installing an OS, 
						not just an IDE.
					</p>
					<h2>What You Need to Get Started</h2>
					<p>
						A Raspberry Pi board, a microSD card (16GB or larger), a USB-C power supply, and a computer 
						to flash the SD card from. A monitor, keyboard, and mouse are optional since the Pi can be 
						set up headless over SSH.
					</p>


					<h2>Initial Setup</h2>

					<p>
						Download and run the official Raspberry Pi Imager on your computer. Select the OS 
						(Raspberry Pi OS is the default choice for beginners), select your SD card, and before 
						writing, open the advanced options to set a hostname, enable SSH, and configure Wi-Fi.
						This allows you to boot the Pi headless and connect remotely.
					</p>


					<pre className='raspberrypi-code'>
						<code>
{`ssh pi@raspberrypi.local`}
						</code>
					</pre>


					<p>
						Once the Pi boots and connects to your network, this command opens a remote terminal session 
						on it. From there, everything below runs directly on the Pi.
					</p>


					<h2>Important Things to Know</h2>

					<p>
						A few commands and habits will save you time later:
					</p>


					<pre className='raspberrypi-code'>
						<code>
{`sudo apt update && sudo apt upgrade`}
						</code>
					</pre>


					<p>
						Run this after first boot and periodically after that. It keeps the OS and installed packages 
						up to date.
					</p>


					<pre className='raspberrypi-code'>
						<code>
{`sudo raspi-config`}
						</code>
					</pre>


					<p>
						This opens the configuration tool where you enable interfaces like the camera, I2C, or SPI, 
						which are disabled by default and required for many hardware projects.
					</p>


					<p>
						Also worth knowing: GPIO pin numbers on the board do not always match the numbers used by 
						Python libraries. Always check whether a library uses BCM numbering or physical board numbering.
					</p>


					<h2>Your First Script: Blinking an LED</h2>

					<p>
						Wire an LED with a 220 ohm resistor between GPIO pin 17 and ground. The 
						<span className='raspberrypi-inline'>gpiozero</span> library, included by default on Raspberry Pi OS, 
						makes controlling hardware simple.
					</p>


					<pre className='raspberrypi-code'>
						<code>
{`from gpiozero import LED
from time import sleep

led = LED(17)

while True:
    led.on()
    sleep(1)
    led.off()
    sleep(1)`}
						</code>
					</pre>


					<p>
						<span className='raspberrypi-inline'>LED(17)</span> refers to GPIO pin 17 using BCM numbering.
						<span className='raspberrypi-inline'>on()</span> and 
						<span className='raspberrypi-inline'>off()</span> switch the LED state, while 
						<span className='raspberrypi-inline'>sleep()</span> pauses execution.
					</p>


					<h2>Reading a Button</h2>

					<p>
						Wire a push button between GPIO pin 2 and ground. The 
						<span className='raspberrypi-inline'>gpiozero</span> library handles the pull-up configuration internally.
					</p>


					<pre className='raspberrypi-code'>
						<code>
{`from gpiozero import Button

button = Button(2)

while True:
    button.wait_for_press()
    print("Button pressed")
    button.wait_for_release()`}
						</code>
					</pre>

					<p>
						<span className='raspberrypi-inline'>wait_for_press()</span> pauses execution until the button 
						is pressed. Larger projects usually use callbacks or event handlers instead of blocking loops.
					</p>

					<img className='raspberrypi-content-image' src={raspberrypi_pins} alt="Raspberry Pi board" />

					<h2>Conclusion</h2>

					<p>
						With SSH access, an updated OS, and two working scripts, you now understand the workflow behind 
						most Raspberry Pi projects: connect remotely, write Python, and control hardware through GPIO.
						From here, most projects replace the LED and button with sensors, motors, or camera modules.<br></br>
						<span className='raspberrypi-link' onClick={() => window.open("https://www.raspberrypi.com/software/", "_blank")}>
							Download the Raspberry Pi OS
						</span><br></br>
						<span className='raspberrypi-link' onClick={() => window.open("https://darttgoblin.github.io/NeuroLink/NeuroLink.Pages/Home/Home.html", "_blank")}>
							NeuroDrive project with Raspberry Pi 3
						</span><br></br>
						<span className='raspberrypi-link' onClick={() => window.open("https://darttgoblin.github.io/PulseTrack/PulseTrack.Pages/Home/Home.html", "_blank")}>
							PulseX project with Raspberry Pi 4
						</span>
					</p>

				</div>

            </section>
		</>
	)
}

export default RaspberryPi